/**
 * What a **form** tab renders inside the board (`specs/M4-V8-forms.md` §2.3).
 *
 * monday shows a live preview with a `Results / Edit form / Share form` toolbar and opens the
 * builder in place `[cap:sandbox-form-view.png]`. Ours does the same: the preview is the very
 * component the public page renders, so what the builder shows is what a visitor gets.
 */
import { useState } from 'react';
import { Icon, PageSkeleton, icons } from '@elizmonday/ui';
import { FormBuilder, type BuilderForm } from './FormBuilder';
import { PublicFormPage } from './PublicFormPage';
import { useFormByView, useFormMutations } from './hooks';
import './builder.css';

export function BoardFormView({ boardId, viewId }: { boardId: string; viewId: string }) {
  const query = useFormByView(boardId, viewId);
  const mutations = useFormMutations();
  const [mode, setMode] = useState<'preview' | 'build'>('preview');
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  if (query.isPending) return <PageSkeleton variant="board" />;
  const form = query.data;
  if (form === null || form === undefined) {
    return (
      <div className="ez-form-empty" data-testid="form-view-missing">
        This form view has no form yet.
      </div>
    );
  }

  const builderForm: BuilderForm = {
    id: form.id,
    token: form.token,
    title: form.title,
    description: form.description,
    boardId: form.boardId,
    questions: form.questions,
    settings: form.settings,
    appearance: form.appearance,
    renderId: '',
    active: form.active,
    url: form.url,
  };

  if (mode === 'build') {
    return (
      <FormBuilder
        form={builderForm}
        onBack={() => setMode('preview')}
        onPreview={() => setMode('preview')}
        onShare={() => {
          if (form.url) {
            void navigator.clipboard?.writeText(form.url).catch(() => undefined);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }
        }}
      />
    );
  }

  return (
    <div className="ez-form-view" data-testid="board-form-view">
      {/*
        **P-034**, measured against monday's own form view on `1802997440`
        (`discovery/ui-reference/parity/1802997440/view-37628439-form-monday.png`): the bar is
        `🕐 Results · Edit form · 🔗 Share form · ⋯`. The response count is monday's *Results*
        screen, not a label in the bar, and `Deactivate form` is inside the `⋯` — as a button of
        its own it was both an invention and the widest thing on the bar.
      */}
      <header className="ez-form-view__toolbar">
        <button type="button" data-testid="form-view-results" disabled title="Coming with M4.V8b">
          {/* monday's clock. The registry's only Time glyph is named for the column that
              uses it (`columnCreationLog`); naming a second alias belongs to `packages/ui`. */}
          <Icon icon={icons.columnCreationLog} size={16} ignoreFocusStyle />
          Results
        </button>
        <span className="ez-form-view__spacer" />
        <button type="button" data-testid="form-view-edit" onClick={() => setMode('build')}>
          <Icon icon={icons.edit} size={16} ignoreFocusStyle />
          Edit form
        </button>
        <button
          type="button"
          className="ez-form-view__share"
          data-testid="form-view-share"
          onClick={() => {
            if (!form.url) return;
            void navigator.clipboard?.writeText(form.url).catch(() => undefined);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        >
          <Icon icon={icons.link} size={16} ignoreFocusStyle />
          {copied ? 'Link copied' : 'Share form'}
        </button>
        <button
          type="button"
          className="ez-form-view__more"
          aria-label="Form options"
          aria-expanded={menuOpen}
          data-testid="form-view-more"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
        </button>
        {menuOpen ? (
          <>
            <button
              type="button"
              className="ez-form-view__scrim"
              aria-label="Close the form options"
              data-testid="form-options-scrim"
              onClick={() => setMenuOpen(false)}
            />
            <div className="ez-form-view__menu" data-testid="form-view-menu" aria-label="Form options">
              <button
                type="button"
                data-testid="form-view-active"
                onClick={() => {
                  setMenuOpen(false);
                  mutations.setActive.mutate({ formId: form.id, active: !form.active });
                }}
              >
                {form.active ? 'Deactivate form' : 'Activate form'}
              </button>
              <span className="ez-form-view__count" data-testid="form-view-count">
                {form.submissionsCount} {form.submissionsCount === 1 ? 'response' : 'responses'}
              </span>
            </div>
          </>
        ) : null}
      </header>

      <div className="ez-form-view__preview" data-testid="form-view-preview" aria-label="Form preview">
        <PublicFormPage initial={{ ...builderForm, renderId: 'preview' }} />
      </div>
    </div>
  );
}
